import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { exportTeamsPlanningEvents, formatApiError } from '../../services/api';
import { useTranslation } from 'react-i18next';

const FORMAT_OPTIONS = [
  { value: 'xlsx', labelKey: 'excelFormat', icon: 'table_view' },
  { value: 'csv', labelKey: 'csv', icon: 'csv', fixedLabel: 'CSV' },
];

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function ExportReportModal({ isOpen, onClose, filters = {} }) {
  const { t } = useTranslation('planning');
  const { t: tCommon } = useTranslation('common');
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setError(null);
    setExporting(false);
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !exporting) onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose, exporting]);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { blob, filename } = await exportTeamsPlanningEvents(filters, format);
      triggerBlobDownload(blob, filename);
      onClose?.();
    } catch (err) {
      const formatted = formatApiError(err);
      const detail = err?.data?.detail;
      const detailText =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => (typeof item === 'string' ? item : item?.string || '')).filter(Boolean).join(' ')
            : detail && typeof detail === 'object'
              ? Object.values(detail).flat().join(' ')
              : null;
      setError(formatted.generalError || detailText || t('failedToExport'));
    } finally {
      setExporting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  const periodLabel =
    filters?.date_from && filters?.date_to
      ? `${filters.date_from} → ${filters.date_to}`
      : t('currentFilters');

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="teams-planning-page fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!exporting) onClose?.();
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-report-title"
            className="w-full max-w-md bg-white rounded-xl border border-[#e5e7eb] shadow-[0_12px_40px_rgba(18,65,145,0.18)] overflow-hidden"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#e5e7eb]">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <span className="material-symbols-outlined text-[20px]">download</span>
                </span>
                <div className="min-w-0">
                  <h2 id="export-report-title" className="text-base font-bold text-[#111418]">
                    {t('exportReportTitle')}
                  </h2>
                  <p className="text-xs text-[#617289] mt-0.5">
                    {t('exportPeriodHint', { period: periodLabel })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-dismiss-btn !p-1.5"
                onClick={onClose}
                aria-label={tCommon('close')}
                disabled={exporting}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            <div className="px-5 py-4 space-y-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#617289] mb-2">
                  {t('format')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {FORMAT_OPTIONS.map((option) => {
                    const active = format === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-xs font-semibold transition-colors ${
                          active
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-[#e5e7eb] text-[#617289] hover:border-primary/40'
                        }`}
                        onClick={() => setFormat(option.value)}
                        aria-pressed={active}
                        disabled={exporting}
                      >
                        <span className="material-symbols-outlined text-[22px]">{option.icon}</span>
                        <span>{option.fixedLabel || t(option.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <p className="text-xs text-[#617289] bg-[#f9fafb] rounded-lg px-3 py-2.5 border border-[#e5e7eb]">
                One row per event: name, email, team, centre, category, title, event from, event to.
              </p>

              {error ? (
                <p className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {typeof error === 'string' ? error : t('failedToExportShort')}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#fafbfc]">
              <button
                type="button"
                className="modal-dismiss-btn"
                onClick={onClose}
                disabled={exporting}
              >
                {tCommon('cancel')}
              </button>
              <button
                type="button"
                className="remind-all-btn"
                onClick={handleExport}
                disabled={exporting}
              >
                <span className={`material-symbols-outlined${exporting ? ' animate-spin' : ''}`}>
                  {exporting ? 'progress_activity' : 'download'}
                </span>
                <span>{exporting ? t('exporting') : tCommon('export')}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
